import { useState } from "react";

import { uploadSupportingDocumentFile } from "@synthesis/shared-services";
import { Button, Modal, NoticeBase, setPromiseSnack } from "@synthesis/shared-ui";
import { observer } from "mobx-react-lite";

import { Uploader, type UploaderListItem } from "../../../../components/uploader";
import { useUserInfo } from "../../../../shared/hooks/use-user-info";
import { useReviewDocuments } from "../../hooks/use-review-documents";
import { usePageStore } from "../../application-detail-page.lib";

const SNACK_TITLES_UPLOAD = {
  pending: "Выполняется загрузка документов...",
  fulfilled: "Документы загружены",
  rejected: "Не удалось загрузить документы. Попробуйте еще раз",
} as const;

type ReviewDocumentUploadModalProps = {
  requestId: string;
  onClose: () => void;
};

const getFileKey = (file: File): string => `${file.name}:${file.size}:${file.lastModified}`;

export const ReviewDocumentUploadModal = ({ requestId, onClose }: ReviewDocumentUploadModalProps) => {
  const userInfo = useUserInfo();
  const { invalidateReviewDocuments } = useReviewDocuments(requestId);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  const handleSelectFiles = (files: File[]) => {
    if (isUploading) {
      return;
    }

    setSelectedFiles((previousFiles) => {
      const knownKeys = new Set(previousFiles.map(getFileKey));
      const appendedFiles = files.filter((file) => !knownKeys.has(getFileKey(file)));

      return [...previousFiles, ...appendedFiles];
    });
  };

  const handleDeleteFromList = (fileMeta: UploaderListItem) => {
    if (!(fileMeta instanceof File)) {
      return;
    }

    setSelectedFiles((previousFiles) => previousFiles.filter((file) => getFileKey(file) !== getFileKey(fileMeta)));
  };

  const handleUpload = () => {
    if (!selectedFiles.length || !userInfo?.id || isUploading) {
      return;
    }

    setIsUploading(true);

    const promise = (async () => {
      for (const file of selectedFiles) {
        await uploadSupportingDocumentFile(
          { sourceId: requestId, sourceType: "INTERNAL_REQUEST_SUPPORTING", filename: file.name, userId: userInfo.id },
          file,
        );
      }
      await invalidateReviewDocuments();
      onClose();
    })();

    setPromiseSnack({ promise, promiseSettings: { title: SNACK_TITLES_UPLOAD } });

    promise
      .catch(() => undefined)
      .finally(() => {
        setIsUploading(false);
      });
  };

  return (
    <Modal.Root isOpen setIsOpen={handleOpenChange}>
      <Modal.Content className="flex max-h-[calc(100vh-4rem)] w-[700px] flex-col overflow-hidden">
        <Modal.Title>Загрузка документов рассмотрения</Modal.Title>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="flex flex-col gap-4 p-8">
            <h3 className="heading-h4">Загрузите сопроводительные документы</h3>
            <p className="paragraph-base text-type-minor">Загрузите телеграммы, акты, письма и иные документы, сопровождающие рассмотрение заявки.</p>

            <NoticeBase
              status="info"
              titleAppearance="common"
              title="Допустимый размер файла — 500Мб"
              description="Файлы размером более 500Мб загрузить не получится."
            />

            <Uploader multiple isViewFileList allowDeleteFromList onSelectFiles={handleSelectFiles} onDeleteFromList={handleDeleteFromList} />
          </div>
        </div>

        <div className="flex shrink-0 gap-4 px-8 pb-6 pt-2">
          <Button type="button" theme="primary" size="lg" disabled={!selectedFiles.length || isUploading} onClick={handleUpload}>
            Загрузить
          </Button>
          <Button type="button" theme="secondary" size="lg" disabled={isUploading} onClick={() => handleOpenChange(false)}>
            Отмена
          </Button>
        </div>
      </Modal.Content>
    </Modal.Root>
  );
};

export const ReviewDocumentUploadModalWrapper = observer(() => {
  const store = usePageStore();
  const popupAction = store?.popupAction;

  if (!popupAction || popupAction.type !== "upload-review-document") {
    return null;
  }

  const handleClose = () => {
    store?.setPopupAction(null);
  };

  return <ReviewDocumentUploadModal requestId={popupAction.requestId} onClose={handleClose} />;
});
